import { MAX_TO, type PriceTier } from '@vy/types';
import {
  extendInIndex,
  isEmptyArray,
  isNotNullOrUndefined,
  isNullOrUndefined,
  isSingleItem,
  newId,
  removeInIndex,
  requiredAt,
  validateNotNullOrUndefined,
  withDefault,
} from '@vy/utils';

const DEFAULT_TIER_INCREMENT = 100;
const DEFAULT_INFINITY_VALUE = MAX_TO;

export function addNewTier(tiers: Partial<PriceTier>[]) {
  if (isEmptyArray(tiers)) {
    return [buildNewTier(1, DEFAULT_INFINITY_VALUE, 0)];
  }

  const lastTier = requiredAt(tiers, -1, 'tiers');
  const isLastCapped = !isInfinity(lastTier.to);

  if (isSingleItem(tiers)) {
    return addNewSingleTier(tiers, isLastCapped);
  }

  const tierBeforeLast = tiers.at(-2);
  const tierIncrement = calculateTierIncrement(lastTier, tierBeforeLast, isLastCapped);

  if (!isLastCapped) {
    validateNotNullOrUndefined(lastTier.from, 'last tier must have a from value');
    lastTier.to = lastTier.from + tierIncrement;
  }

  validateNotNullOrUndefined(lastTier.to, 'last tier must have a to value');
  const newTierFrom = lastTier.to;
  const newTierTo = isLastCapped ? newTierFrom + tierIncrement : DEFAULT_INFINITY_VALUE;

  const newTier = buildNewTier(newTierFrom, newTierTo, lastTier.price);

  const updatedTiers = [...tiers, newTier];

  return adjustTiers(updatedTiers);
}

export function deleteFromTiers(tiers: Partial<PriceTier>[], tierIndex: number) {
  const tierToDelete = requiredAt(tiers, tierIndex, 'tiers to delete');
  const tiersWithUpdatedNextTier = extendInIndex(tiers, tierIndex + 1, {
    from: tierToDelete.from,
  });

  const updatedTiers = removeInIndex(tiersWithUpdatedNextTier, tierIndex);

  return adjustTiers(updatedTiers);
}

export function adjustTiers(tiers: Partial<PriceTier>[]) {
  if (tiers.length === 1) {
    return [
      {
        ...tiers[0],
        from: tiers[0].from ?? 1,
        to: tiers[0].to,
      },
    ];
  }

  const adjustedTiers = tiers
    .slice(0, -1)
    .reduce<Partial<PriceTier>[]>((adjustedTiersAccumulator, tier, index) => {
      const previousTier = adjustedTiersAccumulator.at(index - 1);
      const adjustedTier = adjustTier(previousTier, tier, false);

      return [...adjustedTiersAccumulator, adjustedTier];
    }, []);

  const lastTier = tiers[tiers.length - 1];
  const previousTier = adjustedTiers[adjustedTiers.length - 1];
  const adjustedLastTier = adjustTier(previousTier, lastTier, true);

  return [...adjustedTiers, adjustedLastTier];
}

function adjustTier(
  previousTier: Partial<PriceTier> | undefined,
  currentTier: Partial<PriceTier>,
  isLastTier = false,
) {
  const from = isNullOrUndefined(previousTier)
    ? currentTier.from
    : adjustTierFromValue(previousTier, currentTier);
  const to =
    isLastTier && isNullOrUndefined(currentTier.to)
      ? DEFAULT_INFINITY_VALUE
      : adjustTierToValue({ ...currentTier, from });

  return {
    ...currentTier,
    from,
    to,
  };
}

function adjustTierFromValue({ to: previousTo }: Partial<PriceTier>, { from }: Partial<PriceTier>) {
  const isInvalid = isNotNullOrUndefined(previousTo) && isNotNullOrUndefined(from) && from - 1 !== previousTo;

  return isInvalid ? previousTo : from;
}

function adjustTierToValue({ from, to }: Partial<PriceTier>) {
  const isInvalid = isNotNullOrUndefined(from) && isNotNullOrUndefined(to) && from >= to;

  return isInvalid ? from + 1 : to;
}

function buildNewTier(
  from: number,
  to: number | undefined,
  lastPrice: number | undefined,
): Partial<PriceTier> {
  return {
    from,
    to,
    price: withDefault(lastPrice, 0),
    flatRate: 0,
  };
}

function addNewSingleTier(tiers: Partial<PriceTier>[], isLastCapped: boolean) {
  const firstTier = requiredAt(tiers, 0, 'tiers');

  const to = isLastCapped ? firstTier.to : 100;

  validateNotNullOrUndefined(to, 'first tier must have a to value');
  validateNotNullOrUndefined(firstTier.from, 'first tier must have a from value');

  const firstTierAdjustedFrom = firstTier.from === 1 ? 0 : firstTier.from;
  const increment = to - firstTierAdjustedFrom;
  const newFrom = to;

  return [
    {
      ...tiers[0],
      to,
    },
    {
      from: newFrom,
      to: isLastCapped ? newFrom + increment : DEFAULT_INFINITY_VALUE,
      id: newId(),
      price: 0,
      flatRate: 0,
    },
  ];
}

function calculateTierIncrement(
  lastTier: Partial<PriceTier>,
  tierBeforeLast: Partial<PriceTier> | undefined,
  isLastCapped: boolean,
) {
  if (isNullOrUndefined(tierBeforeLast)) {
    return DEFAULT_TIER_INCREMENT;
  }

  if (isLastCapped) {
    validateNotNullOrUndefined(lastTier.to, 'last tier must have a to value');
    validateNotNullOrUndefined(lastTier.from, 'last tier must have a from value');
    const firstTierAdjustedFrom = lastTier.from === 1 ? 0 : lastTier.from;

    return Math.abs(lastTier.to - firstTierAdjustedFrom);
  }

  validateNotNullOrUndefined(tierBeforeLast.to, 'tier before last must have a to value');
  validateNotNullOrUndefined(tierBeforeLast.from, 'tier before last must have a from value');
  const firstTierAdjustedFrom = tierBeforeLast.from === 1 ? 0 : tierBeforeLast.from;

  return Math.abs(tierBeforeLast.to - firstTierAdjustedFrom);
}

function isInfinity(value: number | undefined) {
  return value === MAX_TO;
}
